<template>
  <div v-show="groupedBanners.length > 0">
    <v-row
      no-gutters
      :justify="
        maxPosition == 6 && $vuetify.breakpoint.mdAndDown ? undefined : 'center'
      "
      :justify-lg="justifyDesktop"
      :class="{
        'my-8': !isTakeoverBanner && !disableYMarginContent,
        ['fipa-banners-content' + forceRow || maxPosition == 6
          ? '-single-row'
          : '']: true
      }"
    >
      <v-col v-for="(banners, i) in groupedBanners" :key="i" class="banner-col">
        <template v-if="banners.length <= 0 && bannersLoading">
          <!--
          <div
            class="d-flex justify-center align-center strong ma-auto mb-4"
            :style="`width: ${width};height: ${height};`"
          >
            <v-progress-circular active indeterminate color="muted" />
          </div>
          -->
        </template>
        <template v-else>
          <AppTakeoverBanner
            v-if="isTakeoverBanner && banners[0]"
            :banner="banners[0]"
          />
          <AppBannerCollection
            v-else
            :banners="banners"
            :min-width="smallScreen ? undefined : width"
            :max-width="width"
            :min-height="smallScreen ? undefined : height"
            :max-height="height"
            :style-image="styleImage"
          />
        </template>
      </v-col>
    </v-row>
  </div>
</template>
<script>
import { getCurrentInstance, ref, computed } from "vue";
import { useQuery } from "@vue/apollo-composable";
import gql from "fraql";
import { sortBy } from "lodash-es";
import AppBannerCollection from "./AppBannerCollection.vue";
import { banners } from "./bannersActive.js";
import BANNER_POSITIONS_MAX_LIST from "@/utils/bannerPositionsMaxList";
import AppTakeoverBanner from "./AppTakeoverBanner.vue";

const bannersVisible = gql`
  query bannersVisible($placements: [String!], $personId: ID) {
    bannersVisible(placements: $placements, personId: $personId) {
      ${banners}
    }
  }
`;

const bannersDefault = gql`
  query banners {
    bannersActive {
      ${banners}
    }
  }
`;

export default {
  name: "AppShowBanners",
  components: { AppBannerCollection, AppTakeoverBanner },
  props: {
    placementName: {
      type: String,
      required: true
    },
    showOnlyPositionNumber: {
      type: Number,
      default: null
    },
    showOnlyPositions: {
      type: Array,
      default: () => []
    },
    largeBanner: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: null
    },
    forceRow: {
      type: Boolean,
      default: false
    },
    overwriteMaxPosition: {
      type: Number,
      default: undefined
    },
    disableYMarginContent: {
      type: Boolean,
      default: false
    },
    bannersVisibleVariables: {
      type: Object,
      default: () => ({})
    }
  },
  setup(props) {
    const instance = getCurrentInstance();
    const bannersActive = ref([]);

    const currentBannerType = computed(() => {
      if (instance.proxy.$vuetify.breakpoint.lgAndUp) {
        return props.placementName + "_DESKTOP";
      }
      return props.placementName + "_MOBILE";
    });

    const query = computed(() => {
      switch (props.placementName) {
        case "PERSON_PROFILE":
          return bannersVisible;
        default:
          return bannersDefault;
      }
    });

    const fetchPolicy = computed(() => {
      switch (props.placementName) {
        case "PERSON_PROFILE":
          return "cache-and-network";
        default:
          return "cache-first";
      }
    });

    const { onResult, bannersLoading } = useQuery(
      () => query.value,
      () => ({
        ...props.bannersVisibleVariables,
        placements: [currentBannerType.value]
      }),
      () => ({ fetchPolicy: fetchPolicy.value })
    );

    onResult(({ loading, data }) => {
      if (loading) return;
      bannersActive.value = data.bannersVisible ?? data.bannersActive ?? [];
    });

    return {
      bannersActive,
      bannersLoading,
      currentBannerType
    };
  },
  computed: {
    isTakeoverBanner() {
      return this.placementName === "TAKEOVER";
    },
    isResponsiveBanner() {
      return this.placementName === "FRONTPAGE_RESPONSIVE";
    },
    smallScreen() {
      return this.$vuetify.breakpoint.xsOnly;
    },
    maxPosition() {
      if (this.overwriteMaxPosition) return this.overwriteMaxPosition;
      if (this.$vuetify.breakpoint.lgAndUp) {
        return BANNER_POSITIONS_MAX_LIST[this.placementName + "_DESKTOP"];
      }
      return BANNER_POSITIONS_MAX_LIST[this.placementName + "_MOBILE"];
    },
    justifyDesktop() {
      if (this.maxPosition >= 6) {
        return "space-around";
      } else {
        return "space-between";
      }
    },
    styleImage() {
      if (this.size) {
        return `max-height: ${this.size};max-width: ${this.size}`;
      }

      switch (this.currentBannerType) {
        case "PERSON_PROFILE_DESKTOP":
          return "width: 400px;height: 100px";
        case "PERSON_PROFILE_MOBILE":
          return "width: 350px;height: 175px";
      }

      if (this.largeBanner) {
        return "max-height: 222px;max-width: initial;object-fit: cover;object-position: center;";
      }
      switch (this.maxPosition) {
        case 6: {
          const css = "min-height: 160px;max-height: 160px;max-width: 160px;";
          if (this.$vuetify.breakpoint.lgAndUp) {
            return css + "padding: 0.75rem;";
          } else {
            return css;
          }
        }
        case 4:
          return "min-height: 255x;max-width: 255px;";
        case 2:
          return "min-height: 270x;max-width: 540px;";
        default: {
          if (this.$vuetify.breakpoint.xsOnly) {
            return "max-width: 100%;";
          }
          return "min-height: 350px;max-width: 350px;";
        }
      }
    },
    width() {
      if (this.size) {
        return this.size;
      }

      switch (this.currentBannerType) {
        case "PERSON_PROFILE_DESKTOP":
          return "400px";
        case "PERSON_PROFILE_MOBILE":
          return "350px";
      }

      if (this.largeBanner) {
        if (this.$vuetify.breakpoint.lgAndUp) {
          return "1110px";
        } else {
          if (this.bannersLoading) {
            return "500px;";
          } else {
            return "100%";
          }
        }
      }

      switch (this.maxPosition) {
        case 6:
          return "160px";
        case 4:
          return "255px";
        case 2:
          return "540px";
        default:
          return "350px";
      }
    },
    height() {
      if (this.size) {
        return this.size;
      }

      switch (this.currentBannerType) {
        case "PERSON_PROFILE_DESKTOP":
          return "100px";
        case "PERSON_PROFILE_MOBILE":
          return "175px";
      }

      if (this.largeBanner) {
        return "222px";
      }

      switch (this.maxPosition) {
        case 6:
          return "160px";
        case 4:
          return "255px";
        case 2:
          return "270px";
        default:
          return "350px";
      }
    },
    desktopOrMobileBanners() {
      const externalContent = ["EXTERNAL_ADNUNTIUS", "EXTERNAL_NIF"];
      if (this.isResponsiveBanner) {
        return this.bannersActive.filter(
          b =>
            b.placement == "FRONTPAGE_RESPONSIVE" &&
            (b.imageUrl || externalContent.includes(b.contentType))
        );
      }

      const type = this.$vuetify.breakpoint.lgAndUp ? "DESKTOP" : "MOBILE";
      return this.bannersActive.filter(
        b =>
          b.placement.includes(type) &&
          (b.imageUrl || externalContent.includes(b.contentType))
      );
    },
    groupedBanners() {
      const grouped = {};
      if (!this.showOnlyPositionNumber) {
        if (this.showOnlyPositions.length > 0) {
          this.showOnlyPositions.forEach(num => {
            grouped[String(num)] = [];
          });
        } else {
          for (let i = 1; Object.keys(grouped).length < this.maxPosition; i++) {
            grouped[String(i)] = [];
          }
        }
      } else {
        grouped[String(this.showOnlyPositionNumber)] = [];
      }

      this.desktopOrMobileBanners
        .filter(b => {
          if (b.placement.includes(this.placementName)) {
            // Filters banners that meet showOnlyPositionNumber or showOnlyPositions props given interger(s)
            if (this.showOnlyPositionNumber) {
              return b.position == this.showOnlyPositionNumber;
            } else if (this.showOnlyPositions.length > 0) {
              return this.showOnlyPositions.includes(b.position);
            }
            return true;
          }

          return false;
        })
        .filter(b => {
          if (b.position) {
            if (!grouped[String(b.position)]) {
              grouped[String(b.position)] = [];
            }
            grouped[String(b.position)].push(b);
            return false;
          }
          return true;
        });

      return Object.entries(grouped)
        .filter(x => {
          if (this.bannersLoading) return true;
          return x[1].length > 0;
        })
        .map(x => sortBy(x[1], "rank"));
    }
  }
};
</script>
<style scoped lang="scss">
.fipa-banners-content {
  display: flex;
  flex-flow: row;
  & > .banner-col {
    padding: 0 8px;
    flex-grow: 1;
  }
  @media (max-width: 960px) {
    & > .banner-col {
      padding: 0;
      padding-bottom: 1rem;
      &:last-child {
        padding-bottom: 0;
      }
    }

    flex-flow: column;
  }
}

.fipa-banners-content-single-row {
  display: grid;
  grid-template-rows: 1fr;
  grid-column-gap: 0.75em;
  @media (max-width: 960px) {
    grid-column-gap: 42px;
  }

  overflow-x: visible;
  margin: 0 8px !important;
  & > .banner-col {
    grid-row: 1 / -1;
    /*
    @media (max-width: 960px) {
      &:first-child {
        padding-left: 20px;
      }

      &:last-child {
        padding-right: 20px;
      }
    }*/
  }
}
</style>
